import type { BulkChange, BulkChangeOp, BulkEditableField } from '@/composables/api/useBulkActionApi';

/**
 * Turning what the user typed in the bulk edit dialog into one `changes` entry of the request.
 *
 * Each control keeps its value as a string, the way the inputs hand it over. This module checks
 * it and converts it to the JSON value the API contract names for the field's type.
 */

export const BULK_OP_LABELS: Record<BulkChangeOp, string> = {
  set: 'Set to',
  clear: 'Clear',
  shift_days: 'Shift by days',
};

/** The contract's range for a shift. Zero is refused, because it would change nothing. */
export const SHIFT_DAYS_LIMIT = 366;

export type BuiltChange = { change: BulkChange; error?: undefined } | { change?: undefined; error: string };

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const WHOLE_NUMBER_PATTERN = /^-?\d+$/;

function isRealDate(value: string): boolean {
  if (!DATE_PATTERN.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

/**
 * A time as `HH:MM:SS`. The user may leave the seconds off, so `9:30` becomes `09:30:00`.
 * Null when the text is not a time of day.
 */
export function normalizeTimeValue(raw: string): string | null {
  const match = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(raw.trim());
  if (!match) return null;
  const [hours, minutes, seconds] = [Number(match[1]), Number(match[2]), Number(match[3] ?? '0')];
  if (hours > 23 || minutes > 59 || seconds > 59) return null;
  return [hours, minutes, seconds].map((part) => String(part).padStart(2, '0')).join(':');
}

/**
 * A datetime as `YYYY-MM-DD HH:MM:SS`, from a `datetime-local` input's `YYYY-MM-DDTHH:MM` value.
 * Null when the text is not a date and a time.
 */
export function normalizeDatetimeValue(raw: string): string | null {
  const match = /^(\d{4}-\d{2}-\d{2})[T ](\d{1,2}:\d{2}(?::\d{2})?)$/.exec(raw.trim());
  if (!match || !isRealDate(match[1])) return null;
  const time = normalizeTimeValue(match[2]);
  return time === null ? null : `${match[1]} ${time}`;
}

/** Check one row of the dialog and build its `changes` entry, or say what is wrong with it. */
export function buildBulkChange(field: BulkEditableField, op: BulkChangeOp | null, rawValue: string | null): BuiltChange {
  if (op === null || !field.operations.includes(op)) {
    return { error: `Choose what to do to ${field.label}.` };
  }

  if (op === 'clear') {
    return { change: { field: field.field, op, value: null } };
  }

  const raw = (rawValue ?? '').trim();

  if (op === 'shift_days') {
    if (!WHOLE_NUMBER_PATTERN.test(raw)) {
      return { error: `Enter a whole number of days for ${field.label}.` };
    }
    const days = Number(raw);
    if (days === 0 || Math.abs(days) > SHIFT_DAYS_LIMIT) {
      return { error: `Shift ${field.label} by 1 to ${SHIFT_DAYS_LIMIT} days, forward or back.` };
    }
    return { change: { field: field.field, op, value: days } };
  }

  if (raw === '') {
    return { error: `Enter a value for ${field.label}. To empty it, choose Clear.` };
  }

  switch (field.type) {
    case 'date':
      return isRealDate(raw)
        ? { change: { field: field.field, op, value: raw } }
        : { error: `Pick a date for ${field.label}.` };
    case 'datetime': {
      const value = normalizeDatetimeValue(raw);
      return value === null
        ? { error: `Pick a date and time for ${field.label}.` }
        : { change: { field: field.field, op, value } };
    }
    case 'time': {
      const value = normalizeTimeValue(raw);
      return value === null
        ? { error: `Enter ${field.label} as HH:MM:SS, for example 09:00:00.` }
        : { change: { field: field.field, op, value } };
    }
    case 'boolean':
      if (raw !== 'true' && raw !== 'false') {
        return { error: `Choose Yes or No for ${field.label}.` };
      }
      return { change: { field: field.field, op, value: raw === 'true' } };
    case 'integer':
      return WHOLE_NUMBER_PATTERN.test(raw)
        ? { change: { field: field.field, op, value: Number(raw) } }
        : { error: `Enter a whole number for ${field.label}.` };
    case 'lookup': {
      const id = Number(raw);
      return Number.isInteger(id) && id > 0
        ? { change: { field: field.field, op, value: id } }
        : { error: `Choose a value for ${field.label}.` };
    }
    default:
      if (field.max_length !== null && raw.length > field.max_length) {
        return { error: `${field.label} can be at most ${field.max_length} characters.` };
      }
      return { change: { field: field.field, op, value: raw } };
  }
}

/** A one-line description of a change, for the confirmation. */
export function describeBulkChange(change: BulkChange, field: BulkEditableField | undefined, valueLabel?: string): string {
  const label = field?.label ?? change.field;
  if (change.op === 'clear') return `Clear ${label}`;
  if (change.op === 'shift_days') {
    const days = Number(change.value);
    const size = Math.abs(days);
    return `Shift ${label} ${days > 0 ? 'forward' : 'back'} ${size} day${size === 1 ? '' : 's'}`;
  }
  let shown: string;
  if (valueLabel !== undefined) shown = valueLabel;
  else if (typeof change.value === 'boolean') shown = change.value ? 'Yes' : 'No';
  else shown = String(change.value);
  return `Set ${label} to ${shown}`;
}
